import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  blogService,
  BlogPost,
  getLocalizedString,
} from "../../services/firebaseService";
import DetailLayout from "../DetailLayout/DetailLayout";

const BlogDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [blog, setBlog] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { i18n } = useTranslation();
  const currentLang = i18n.language || "en";

  useEffect(() => {
    if (!id) {
      navigate("/blog");
      return;
    }

    loadBlogPost(id);
  }, [id, navigate]);

  const loadBlogPost = async (blogId: string) => {
    try {
      setLoading(true);
      const blogPost = await blogService.getBlogPostById(blogId);
      if (blogPost) {
        setBlog(blogPost);
        setError(null);
      } else {
        setError("Blog post not found");
      }
    } catch (err) {
      console.error("Error loading blog post:", err);
      setError("Failed to load blog post");
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    loadBlogPost(id!);
  };

  return (
    <DetailLayout
      loading={loading}
      error={error}
      notFoundTitle="Blog Post Not Found"
      // notFoundMessage="The blog post you are looking for does not exist."
      backPath="/blog"
      backLabel="Back to Blog"
      onRetry={handleRetry}
    >
      {blog &&
        (() => {
          const localizedTitle = getLocalizedString(blog.title, currentLang);
          const localizedExcerpt = getLocalizedString(
            blog.excerpt,
            currentLang
          );
          const localizedContent = getLocalizedString(
            blog.content,
            currentLang
          );

          return (
            <div className="w-full">
              <article className=" mx-auto px-4 md:px-6 lg:px-8 py-6">
                {/* Title */}
                <header className="mb-8">
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 text-[var(--text-primary)] leading-tight tracking-tight">
                    {localizedTitle}
                  </h1>

                  {/* Author + Date */}
                  <div className="flex flex-wrap items-center gap-4 mb-6 text-sm md:text-base">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--background-secondary)] text-[var(--text-secondary)]">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      <span className="font-medium">{blog.author}</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--background-secondary)] text-[var(--text-secondary)]">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span>
                        {blog.createdAt?.toDate().toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </span>
                    </div>
                  </div>

                  {/* Tags */}
                  {blog.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {blog.tags.map((tag, i) => (
                        <span
                          key={i}
                          className="px-3 py-1.5 rounded-full text-xs md:text-sm font-medium bg-[var(--background-secondary)] text-[var(--text-secondary)] border border-[var(--border-color)] hover:bg-[var(--primary-color)] hover:text-white hover:border-[var(--primary-color)] transition-all duration-200 cursor-default"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </header>

                {/* Image */}
                {blog.image && (
                  <div className="w-full aspect-video rounded-2xl overflow-hidden mb-10 bg-[var(--background-secondary)] shadow-xl border border-[var(--border-color)]">
                    <img
                      src={blog.image}
                      alt={localizedTitle}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "/images/placeholder.png";
                      }}
                    />
                  </div>
                )}

                {/* Summary/Excerpt */}
                {localizedExcerpt && (
                  <div className="mb-10 p-6 md:p-8 rounded-2xl bg-gradient-to-r from-[var(--background-secondary)] to-transparent border-l-4 border-[var(--primary-color)] shadow-sm">
                    <p className="text-lg md:text-xl leading-relaxed text-[var(--text-secondary)] italic m-0">
                      {localizedExcerpt}
                    </p>
                  </div>
                )}

                {/* Content */}
                <div className="blog-content">
                  <div
                    className="prose prose-lg max-w-none leading-relaxed
                      [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:mt-12 [&_h1]:mb-6 [&_h1]:text-[var(--text-primary)]
                      [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:text-[var(--text-primary)]
                      [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-[var(--text-primary)]
                      [&_h4]:text-lg [&_h4]:font-semibold [&_h4]:mt-6 [&_h4]:mb-3 [&_h4]:text-[var(--text-primary)]
                      [&_p]:my-6 [&_p]:text-[var(--text-secondary)] [&_p]:leading-relaxed [&_p]:text-base [&_p]:md:text-lg
                      [&_ul]:pl-6 [&_ul]:my-6 [&_ul]:list-disc [&_ul]:space-y-2
                      [&_ol]:pl-6 [&_ol]:my-6 [&_ol]:list-decimal [&_ol]:space-y-2
                      [&_li]:my-2 [&_li]:text-[var(--text-secondary)] [&_li]:leading-relaxed
                      [&_blockquote]:border-l-4 [&_blockquote]:border-[var(--primary-color)] [&_blockquote]:pl-6 [&_blockquote]:pr-4 [&_blockquote]:italic [&_blockquote]:my-8 [&_blockquote]:text-[var(--text-secondary)] [&_blockquote]:bg-[var(--background-secondary)] [&_blockquote]:py-4 [&_blockquote]:rounded-r-lg [&_blockquote]:shadow-sm
                      [&_pre]:bg-[var(--background-secondary)] [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:my-6 [&_pre]:border [&_pre]:border-[var(--border-color)] [&_pre]:shadow-sm
                      [&_code]:bg-[var(--background-secondary)] [&_code]:px-2 [&_code]:py-1 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono [&_code]:text-[var(--primary-color)] [&_code]:border [&_code]:border-[var(--border-color)]
                      [&_a]:text-[var(--primary-color)] [&_a]:hover:text-[var(--primary-hover)] [&_a]:hover:underline [&_a]:font-medium [&_a]:transition-colors
                      [&_img]:rounded-xl [&_img]:my-8 [&_img]:shadow-lg [&_img]:w-full [&_img]:h-auto
                      [&_strong]:font-bold [&_strong]:text-[var(--text-primary)]
                      [&_em]:italic [&_em]:text-[var(--text-secondary)]
                      [&_table]:w-full [&_table]:my-6 [&_table]:border-collapse
                      [&_table_th]:border [&_table_th]:border-[var(--border-color)] [&_table_th]:px-4 [&_table_th]:py-2 [&_table_th]:bg-[var(--background-secondary)] [&_table_th]:text-left [&_table_th]:font-semibold
                      [&_table_td]:border [&_table_td]:border-[var(--border-color)] [&_table_td]:px-4 [&_table_td]:py-2 [&_table_td]:text-[var(--text-secondary)]
                      [&_hr]:my-8 [&_hr]:border-t [&_hr]:border-[var(--border-color)]
                      [&_canvas]:max-w-full [&_canvas]:h-auto [&_canvas]:rounded-lg [&_canvas]:border [&_canvas]:border-[var(--border-color)]
                      [&_iframe]:max-w-full [&_iframe]:rounded-lg [&_iframe]:my-6"
                    dangerouslySetInnerHTML={{ __html: localizedContent }}
                  />
                </div>

                <div className="h-12" />
              </article>
            </div>
          );
        })()}
    </DetailLayout>
  );
};

export default BlogDetail;
